// The check-out form is described by the backend as a single ordered list of `fields`.
// These helpers walk that list to build everything Formik needs: the initial values, the
// question-response subset (fields with a questionId), and the validation schema.
//
// Data fields (choice / input / textarea / photo) are handled generically. A few "slot"
// markers tell us where the technician's root-cause widgets go; their initial values and
// branching validation stay frontend-owned because that logic is bespoke.
import * as Yup from 'yup'
import * as Types from 'types'
import questionValidationSchema from '../../utils/questionValidationSchema'
import checkIfRootCauseNeglectOrDefect from '../../utils/checkIfRootCauseNeglectOrDefect'
import findImagesOfType from '../../utils/findImagesOfType'

const { CHECKOUT_SLOT, CHECKOUT_SLOT_TYPE, PHOTO_TYPE, QUESTION_TYPE } = Types

function isSlot(field) {
  return field.type === CHECKOUT_SLOT_TYPE
}

function hasSlot(fields, slot) {
  return fields.some((field) => isSlot(field) && field.slot === slot)
}

// Fields that persist as QuestionResponse rows (they carry a questionId). Status, notes,
// and the default photos are also "questions" on screen, but they submit as trip columns /
// trip photos, so they are not in this list.
export function getCheckoutQuestionResponseFields(fields = []) {
  return fields.filter((field) => field.questionId)
}

// The backend-described photo fields (the default check-out photos plus any custom photo
// questions). Their values are never submitted with the trip or the question responses --
// photos upload as soon as they're picked -- so the submit split uses this to exclude them.
// Discovering them from `fields` by type means a new backend photo field is handled without
// editing the submit code.
export function getCheckoutPhotoFieldNames(fields = []) {
  return fields
    .filter((field) => !isSlot(field) && field.type === QUESTION_TYPE.PHOTO)
    .map((field) => field.fieldName)
}

// Photos are uploaded as soon as they're picked, so a photo field's initial value is
// seeded from the trip's already-uploaded images to keep them visible on re-mount.
function seedPhotoValue(images, { photoType, questionId }) {
  return photoType === PHOTO_TYPE.CUSTOM
    ? findCustomImagesForQuestion(images, questionId)
    : findImagesOfType(images, photoType)
}

function findCustomImagesForQuestion(images, customQuestionId) {
  return images
    .filter(({ questionId }) => questionId === customQuestionId)
    .map(({ filename, id, url }) => ({
      id,
      name: filename,
      type: 'image/*',
      url,
    }))
}

export function buildCheckoutInitialValues(fields = [], trip) {
  const images = trip?.currentTripImages || []

  return fields.reduce((values, field) => {
    if (isSlot(field)) {
      if (field.slot === CHECKOUT_SLOT.ROOT_CAUSE_SELECTS) {
        values.rootCause = ''
        values.operable = ''
      } else if (field.slot === CHECKOUT_SLOT.ROOT_CAUSE_DETAILS) {
        values.abuseNeglectClaims = ''
        values.abusePhotos = findImagesOfType(images, PHOTO_TYPE.ABUSE)
        values.defectPhotos = findImagesOfType(images, PHOTO_TYPE.DEFECT)
      }
      return values
    }

    values[field.fieldName] =
      field.type === QUESTION_TYPE.PHOTO ? seedPhotoValue(images, field) : ''
    return values
  }, {})
}

// Root Cause + Operable are always required together when the selects slot is present.
function rootCauseSelectsSchema() {
  return {
    rootCause: Yup.string().required("Root Cause can't be blank"),
    operable: Yup.string().required("Operable can't be blank"),
  }
}

// The abuse/defect notes and photos are only required once the chosen root cause calls
// for them. A photo field drops its minimum after an upload was attempted (tracked in
// excludedKeys) so a failed upload can't trap the user, but the maximum always applies.
function rootCauseDetailsSchema(excludedKeys) {
  const isExcluded = (key) => excludedKeys.includes(key)

  return {
    abuseNeglectClaims: Yup.string().when('rootCause', {
      is: (value) => checkIfRootCauseNeglectOrDefect(value),
      then: () => Yup.string().required("Notes can't be blank"),
    }),
    abusePhotos: Yup.array().when('rootCause', {
      is: (value) => Types.ROOT_CAUSE_NEGLECT_OPTIONS.includes(value),
      then: () => {
        let schema = Yup.array().max(
          3,
          'Abuse Photo requires between 1 and 3 photo uploads'
        )
        if (!isExcluded('abusePhotos')) {
          schema = schema.min(1, 'Abuse Photo requires at least 1 photo upload')
        }
        return schema
      },
    }),
    defectPhotos: Yup.array().when('rootCause', {
      is: (value) => value === Types.ROOT_CAUSE_DEFECT,
      then: () => {
        let schema = Yup.array().max(
          3,
          'Defect Photo Photo requires between 1 and 3 photo uploads'
        )
        if (!isExcluded('defectPhotos')) {
          schema = schema.min(
            1,
            'Defect Photo Photo requires at least 1 photo upload'
          )
        }
        return schema
      },
    }),
  }
}

export function buildCheckoutValidationSchema(fields = [], excludedKeys = []) {
  // Data fields validate straight from their API constraints; slots contribute the
  // bespoke, frontend-owned root-cause rules.
  const dataFields = fields.filter((field) => !isSlot(field))
  const schema = { ...questionValidationSchema(dataFields, excludedKeys) }

  if (hasSlot(fields, CHECKOUT_SLOT.ROOT_CAUSE_SELECTS)) {
    Object.assign(schema, rootCauseSelectsSchema())
  }
  if (hasSlot(fields, CHECKOUT_SLOT.ROOT_CAUSE_DETAILS)) {
    Object.assign(schema, rootCauseDetailsSchema(excludedKeys))
  }

  return Yup.object(schema)
}
